/**
 * One definition of the planner's clock questions.
 *
 * "Is this on today's board", "what day is this ISO on", "what does the
 * server want a local time to look like" and "where does the visible week
 * end" were each answered in two or three places with slightly different
 * rules; they are answered here once and imported everywhere.
 */
import { addDays, format, parseISO, startOfDay } from "date-fns";

/**
 * Whether [startIso, endIso) touches the calendar day `day` (plant-local).
 * A zero-length span counts when it starts on the day, so an order with no
 * end time still shows up on the day it starts.
 */
export function overlapsDay(startIso: string, endIso: string | null | undefined, day: Date): boolean {
	const dayStart = startOfDay(day).getTime();
	const dayEnd = startOfDay(addDays(day, 1)).getTime();
	const start = parseISO(startIso).getTime();
	const end = endIso ? parseISO(endIso).getTime() : start;
	return start < dayEnd && (end > dayStart || start >= dayStart);
}

/** yyyy-MM-dd of a local Date or an ISO instant, in the plant-local zone. */
export const localDay = (value: Date | string): string =>
	format(typeof value === "string" ? parseISO(value) : value, "yyyy-MM-dd");

/** Midnight that opens the calendar day `day`, as epoch ms — the origin of a board's hour axis. */
export const dayStartMs = (day: Date): number => startOfDay(day).getTime();

/** Decimal hours from `dayStart` (epoch ms) to an ISO instant: 07:30 on the day is 7.5, 01:00 the next day is 25. */
export const hoursInto = (iso: string, dayStart: number): number => (parseISO(iso).getTime() - dayStart) / 3600000;

/** The same for a Date. */
export const hoursIntoDate = (date: Date, dayStart: number): number => (date.getTime() - dayStart) / 3600000;

/** "07:30" from decimal hours into the day; hours past 24 wrap, so 25.5 reads "01:30". */
export const hhmm = (hours: number): string => {
	const h = Math.floor(hours);
	const m = Math.round((hours - h) * 60);
	return `${String(h % 24).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/**
 * Plant-local wall-clock ISO ("2026-10-08T14:30:00", no zone). The planner API
 * stores and compares naive local times; a UTC "Z" string would land hours off.
 */
export function toLocalIso(date: Date): string {
	const pad = (n: number): string => String(n).padStart(2, "0");
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/**
 * The visible week runs Monday to Sunday: `weekStart` is its Monday and the
 * week ends on the Sunday six days later. Every "for the week" range — the
 * default "Until…" date, a work-center chip dropped on a worker, the standing
 * crew form, the pins fetch — reads from this rule.
 */
export const WEEK_END_RULE = "sunday" as const;

/** The Sunday that closes the week starting on `weekStart` (its Monday). */
export const weekEnd = (weekStart: Date): Date => addDays(weekStart, 6);

/** yyyy-MM-dd of that Sunday. */
export const weekEndIso = (weekStart: Date): string => format(weekEnd(weekStart), "yyyy-MM-dd");
